import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrregCompanyNameField } from "@/components/new-project/BrregCompanyNameField";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useCreateManualLead, useLeadDuplicate } from "@/hooks/use-manual-lead";
import { companyFromBrregHit, duplicateDetail, duplicateTitle, type ManualLeadCompany } from "@/lib/manual-lead";

interface NewLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_CONTACT = { full_name: "", title: "", phone: "", email: "" };

/**
 * «Nytt lead» (VIB-3020): ett selskap fra Brønnøysund, valgfri kontaktperson, og hvem som ringer.
 * Liste velges bare av de med Leadlister; andre legger i «Manuelle». Samme duplikatsjekk som importen.
 */
export default function NewLeadDialog({ open, onOpenChange }: NewLeadDialogProps) {
  const canManageLists = useCanAccessMenu("salgsmuligheter.leadlister");
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState<ManualLeadCompany | null>(null);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [listId, setListId] = useState<string | null>(null);
  const [reserve, setReserve] = useState(true);
  const { data: lists = [] } = useLeadLists({ enabled: open && canManageLists });
  const { data: duplicate, isFetching: checking } = useLeadDuplicate(company?.organization_number ?? null);
  const create = useCreateManualLead();

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setCompany(null);
    setContact(EMPTY_CONTACT);
    setListId(null);
    setReserve(true);
  }, [open]);

  const manualList = lists.find((l) => l.source === "manual");
  const selectableLists = lists.filter((l) => l.is_active);
  const canSubmit = !!company && !checking && !duplicate && !create.isPending;

  const submit = () => {
    if (!company) return;
    create.mutate(
      { company, contact: contact.full_name.trim() ? contact : null, listId: canManageLists ? listId ?? manualList?.id ?? null : null, reserve },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Nytt lead</DialogTitle>
          <DialogDescription>Søk opp selskapet i Brønnøysund. Navn, bransje, kommune og ansatte fylles ut.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Selskap</Label>
            {company ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{company.name}</span> <span className="text-muted-foreground">{company.organization_number}</span>
                </span>
                <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setCompany(null)}>
                  Bytt
                </Button>
              </div>
            ) : (
              <>
                <BrregCompanyNameField name={search} onNameChange={setSearch} onHitSelect={(hit) => setCompany(companyFromBrregHit(hit))} />
                <p className="text-xs text-muted-foreground">Navn eller org.nr. (9 siffer).</p>
              </>
            )}
          </div>

          {company && duplicate ? (
            <div role="alert" className="space-y-1 rounded-md border-[1.5px] border-warning bg-warning/10 p-3 text-sm">
              <p className="font-medium">{duplicateTitle(duplicate)}</p>
              <p>{duplicateDetail(duplicate)}</p>
              {duplicate.kind === "customer" && duplicate.customer_id && (
                <Link to={`/kunde/${duplicate.customer_id}`} className="text-primary underline underline-offset-4">
                  Åpne kunden
                </Link>
              )}
            </div>
          ) : company ? (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-border p-3 text-sm">
                {[
                  ["Bransje", [company.industry_code, company.industry_description].filter(Boolean).join(" ")],
                  ["Kommune", company.municipality],
                  ["Ansatte", company.employees?.toString()],
                  ["Nettside", company.website],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="font-medium break-words">{value || "–"}</dd>
                  </div>
                ))}
              </dl>

              <fieldset className="space-y-1.5">
                <legend className="text-sm font-medium">Kontaktperson (valgfritt)</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input aria-label="Navn" placeholder="Navn" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} />
                  <Input aria-label="Tittel" placeholder="Tittel" value={contact.title} onChange={(e) => setContact({ ...contact, title: e.target.value })} />
                  <Input aria-label="Telefon" placeholder="Telefon" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                  <Input aria-label="E-post" placeholder="E-post" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </div>
              </fieldset>

              {canManageLists && (
                <div className="space-y-1.5">
                  <Label htmlFor="new-lead-list">Liste</Label>
                  <Select value={listId ?? manualList?.id ?? ""} onValueChange={setListId}>
                    <SelectTrigger id="new-lead-list">
                      <SelectValue placeholder="Manuelle" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableLists.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Hvem ringer</Label>
                <RadioGroup value={reserve ? "me" : "all"} onValueChange={(v) => setReserve(v === "me")} className="grid gap-2 sm:grid-cols-2">
                  <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 text-sm has-[[data-state=checked]]:border-primary">
                    <RadioGroupItem value="me" className="mt-0.5" />
                    <span>
                      <span className="font-medium">Jeg ringer selv</span>
                      <span className="block text-xs text-muted-foreground">Kommer som ditt neste lead. Ringer du ikke innen to dager, går det til alle.</span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 text-sm has-[[data-state=checked]]:border-primary">
                    <RadioGroupItem value="all" className="mt-0.5" />
                    <span>
                      <span className="font-medium">Til alle</span>
                      <span className="block text-xs text-muted-foreground">Legges i køen som et vanlig lead.</span>
                    </span>
                  </label>
                </RadioGroup>
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {duplicate ? "Lukk" : "Avbryt"}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            Legg til lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
